import { REPORT, REPORT_RANGE } from '@consts/reports';
import { BaseReport } from '../BaseReport';
import { useReportLoad } from '@hooks/useReportLoad';
import { average, countUnique, sum } from '@utils/summaries';
import { basicSort, dateSort } from '@utils/sort';
import { styleDateRange } from '@utils/styles';

interface MaterialCutsByLocationProps {
    dateRange: { startDate: string; endDate: string };
}

export const MaterialCutsByLocation = ({ dateRange }: MaterialCutsByLocationProps) => {
    const { data, hasData, loading } = useReportLoad({
        reportType: REPORT.LOCATION_MATERIAL_TYPE_CUTS_BY_LOCATION,
        params: { ...dateRange, range: 'monthly' },
    });

    const productNames = Array.from(new Set(data.map((item) => item.productName))).sort();

    const rowsByLocation = data.reduce(
        (acc, item) => {
            const key = `${item.date}|${item.companyName}|${item.resellerName}|${item.locationName}`;

            const existing = acc[key] || {
                date: item.date,
                companyName: item.companyName,
                resellerName: item.resellerName,
                locationName: item.locationName,
                totalCuts: 0,
                products: {} as Record<string, number>,
            };

            existing.totalCuts += item.successfulCuts;
            existing.products[item.productName] = item.productPct;

            acc[key] = existing;
            return acc;
        },
        {} as Record<
            string,
            {
                date: string;
                companyName: string;
                resellerName: string;
                locationName: string;
                totalCuts: number;
                products: Record<string, number>;
            }
        >
    );

    const tableData = Object.values(rowsByLocation).map(
        ({ date, companyName, resellerName, locationName, totalCuts, products }) => [
            date,
            companyName,
            resellerName,
            locationName,
            totalCuts,
            ...productNames.map((name) => products[name] ?? 0),
        ]
    );

    const headers = [
        'Date',
        'Company Name',
        'Reseller Name',
        'Location Name',
        'Successful Cuts',
        ...productNames.map((name) => `${name} %`),
    ];

    const summaries = [
        countUnique,
        countUnique,
        countUnique,
        countUnique,
        sum,
        ...productNames.map(() => average),
    ];

    const sort = [
        dateSort(REPORT_RANGE.MONTHLY),
        basicSort,
        basicSort,
        basicSort,
        basicSort,
        ...productNames.map(() => basicSort),
    ];

    return (
        <BaseReport
            title={'Material Type Cuts by Location'}
            description={
                'This table shows you the % breakdown of each material type by location for the time period selected in the date range above.'
            }
            rangeText={styleDateRange([dateRange.startDate, dateRange.endDate])}
            loading={loading}
            hasData={hasData}
            tableOptions={{
                headers,
                data: tableData,
                summaries,
                sort,
            }}
        />
    );
};
